import { AttachQuestionsDialog, DeleteExamDialog, ExamDialog } from '@/components/exams';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import { QuestionPreview } from '@/components/questions';
import { AttachStudentDialog } from '@/components/students';
import {
  useArchiveExam,
  useAttachQuestionsToExam,
  useAttachStudentToExam,
  useDeleteExam,
  useDetachStudentFromExam,
  useExam,
  useGenerateExamPdf,
  useUpdateExam,
} from '@/hooks/useExams';
import type { AppContext } from '@/main';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ui/components';
import { Button } from '@ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ui/components/card';
import { Input } from '@ui/components/input';
import {
  Archive,
  BookOpen,
  Download,
  Edit2,
  ExternalLinkIcon,
  FileText,
  Plus,
  Save,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_dashboard/exams/$examId')<AppContext>({
  component: RouteComponent,
  loader: async ({ params }) => {
    return {
      crumb: `Exam ${params.examId} Details`,
    };
  },
});

function RouteComponent() {
  const { examId } = Route.useParams();
  const { data: exam, isLoading, error } = useExam(examId);
  const updateExamMutation = useUpdateExam();
  const deleteExamMutation = useDeleteExam();
  const archiveExamMutation = useArchiveExam();
  const attachStudentMutation = useAttachStudentToExam();
  const detachStudentMutation = useDetachStudentFromExam();
  const attachQuestionsMutation = useAttachQuestionsToExam();
  const generatePdfMutation = useGenerateExamPdf();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAttachStudentDialogOpen, setIsAttachStudentDialogOpen] = useState(false);
  const [isAttachQuestionsDialogOpen, setIsAttachQuestionsDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    examId: null as string | null,
    examName: '',
  });

  const handleUpdateExam = async (data: { name: string }) => {
    await updateExamMutation.mutateAsync({ examId, data });
  };

  const handleDeleteExam = async (examId: string) => {
    await deleteExamMutation.mutateAsync(examId);
    // Navigate back to exams list
    window.location.href = '/exams';
  };

  const handleAttachStudent = async (studentId: string) => {
    await attachStudentMutation.mutateAsync({ examId, studentId, type: 'attach' });
  };

  const handleDetachStudent = async (studentId: string) => {
    await detachStudentMutation.mutateAsync({ examId, studentId });
  };

  const handleAttachQuestions = async (questionIds: string[]) => {
    await attachQuestionsMutation.mutateAsync({ examId, questionIds, type: 'attach' });
  };

  const handleDetachQuestion = async (questionId: string) => {
    await attachQuestionsMutation.mutateAsync({
      examId,
      questionIds: [questionId],
      type: 'detach',
    });
  };

  const openDeleteDialog = () => {
    if (!exam) return;
    setDeleteDialog({
      open: true,
      examId: exam.id,
      examName: exam.name,
    });
  };

  const handleSave = async () => {
    if (!editName.trim()) return;
    try {
      await updateExamMutation.mutateAsync({ examId, data: { name: editName } });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update exam:', error);
    }
  };

  const handleArchive = async () => {
    if (confirm('Are you sure you want to archive this exam?')) {
      try {
        await archiveExamMutation.mutateAsync(examId);
      } catch (error) {
        console.error('Failed to archive exam:', error);
      }
    }
  };

  const handleGeneratePdf = async () => {
    if (!exam || !exam.questions || exam.questions.length === 0) {
      alert('This exam has no questions to export.');
      return;
    }

    try {
      await generatePdfMutation.mutateAsync({
        examId,
        examName: exam.name,
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading exam details...</div>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load exam details</div>
        <Button asChild variant="outline">
          <Link to="/exams">Back to Exams</Link>
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          {isEditing ? (
            <div className="flex items-center space-x-2">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-2xl font-bold"
                autoFocus
              />
              <Button size="sm" onClick={handleSave} disabled={updateExamMutation.isPending}>
                <Save className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <PageHeaderTitle>{exam.name}</PageHeaderTitle>
              <Button size="sm" variant="ghost" onClick={() => setIsEditDialogOpen(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              {exam.archived && (
                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  <Archive className="h-3 w-3 mr-1" />
                  Archived
                </span>
              )}
            </div>
          )}
          <PageHeaderDescription>
            <span className="text-xs font-mono bg-muted px-2 py-1 rounded">{exam.id}</span>
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={handleGeneratePdf}
            disabled={
              generatePdfMutation.isPending || !exam.questions || exam.questions.length === 0
            }
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            {generatePdfMutation.isPending ? 'Generating...' : 'Export PDF'}
          </Button>
          <Button
            variant="outline"
            disabled={exam.archived}
            onClick={handleArchive}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            {exam.archived ? 'Archived' : 'Archive'}
          </Button>
          <Button variant="destructive" onClick={openDeleteDialog} className="gap-2">
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button onClick={() => setIsEditDialogOpen(true)} className="gap-2">
            <Edit2 className="h-4 w-4" />
            Edit
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="students">Students</TabsTrigger>
          <TabsTrigger value="courses">Courses</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="grid gap-6 md:grid-cols-4">
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Students</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{exam.students?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Taking this exam</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Courses</CardTitle>
                <BookOpen className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{exam.courses?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Associated courses</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Questions</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{exam.questions?.length || 0}</div>
                <p className="text-xs text-muted-foreground">In this exam</p>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div
                  className={`text-2xl font-bold ${exam.archived ? 'text-yellow-600' : 'text-green-600'}`}
                >
                  {exam.archived ? 'Archived' : 'Active'}
                </div>
                <p className="text-xs text-muted-foreground">Exam status</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="students">
          {/* Students */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Students
              </CardTitle>
              <CardDescription>Students taking this exam</CardDescription>
              <CardAction className="flex justify-end">
                <Button
                  onClick={() => setIsAttachStudentDialogOpen(true)}
                  className="gap-2"
                  disabled={exam.archived}
                >
                  <Plus className="h-4 w-4" />
                  Add Students
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>
              {exam.students && exam.students.length > 0 ? (
                <div className="space-y-2">
                  {exam.students.map((student, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 border rounded hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">Student {index + 1}</div>
                          <div className="text-sm text-muted-foreground">Enrolled</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDetachStudent(student)}
                          disabled={exam.archived}
                          className="gap-1"
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No students enrolled</h3>
                  <p className="text-muted-foreground text-center">
                    Students will appear here once they are enrolled in this exam
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAttachStudentDialogOpen(true)}
                    className="mt-4 gap-2"
                    disabled={exam.archived}
                  >
                    <Plus className="h-4 w-4" />
                    Add Students
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="courses">
          {/* Courses */}
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Courses
              </CardTitle>
              <CardDescription>Courses associated with this exam</CardDescription>
            </CardHeader>
            <CardContent>
              {exam.courses && exam.courses.length > 0 ? (
                <div className="flex w-full flex-col gap-2">
                  {exam.courses.map((course) => (
                    <Item key={`courses#${course}`} variant="outline" size="sm" asChild>
                      <Link to="/courses/$courseId" params={{ courseId: course }}>
                        <ItemMedia variant="icon" className="size-12">
                          <BookOpen className="size-6" />
                        </ItemMedia>
                        <ItemContent>
                          <ItemTitle>{course}</ItemTitle>
                          <ItemDescription>Available</ItemDescription>
                        </ItemContent>
                        <ItemActions>
                          <ExternalLinkIcon className="size-4" />
                        </ItemActions>
                      </Link>
                    </Item>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <BookOpen className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No courses associated</h3>
                  <p className="text-muted-foreground text-center">
                    Associate courses with this exam to organize your curriculum
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="questions">
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Questions
              </CardTitle>
              <CardDescription>Questions included in this exam</CardDescription>
              <CardAction className="flex justify-end">
                <Button
                  onClick={() => setIsAttachQuestionsDialogOpen(true)}
                  className="gap-2"
                  disabled={exam.archived}
                >
                  <Plus className="h-4 w-4" />
                  Add Questions
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>
              {exam.questions && exam.questions.length > 0 ? (
                <div className="flex w-full flex-col gap-2">
                  {exam.questions.map((questionId) => (
                    <QuestionPreview
                      key={`questions#${questionId}`}
                      questionId={questionId}
                      onRemove={handleDetachQuestion}
                      showRemoveButton={!exam.archived}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No questions added</h3>
                  <p className="text-muted-foreground text-center">
                    Add questions to create your exam content
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAttachQuestionsDialogOpen(true)}
                    className="mt-4 gap-2"
                    disabled={exam.archived}
                  >
                    <Plus className="h-4 w-4" />
                    Add Questions
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="settings">{/* Settings Content */}</TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ExamDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        exam={exam}
        onSave={handleUpdateExam}
      />

      <DeleteExamDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        examId={deleteDialog.examId}
        examName={deleteDialog.examName}
        onDelete={(item) => handleDeleteExam(item)}
      />

      <AttachStudentDialog
        open={isAttachStudentDialogOpen}
        onOpenChange={setIsAttachStudentDialogOpen}
        title="Attach Student to Exam"
        description="Select a student to attach to this exam."
        excludeStudentIds={exam.students || []}
        onAttach={handleAttachStudent}
      />

      <AttachQuestionsDialog
        open={isAttachQuestionsDialogOpen}
        onOpenChange={setIsAttachQuestionsDialogOpen}
        excludeQuestionIds={exam.questions || []}
        onAttach={handleAttachQuestions}
      />
    </Page>
  );
}
